import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { GET } from "Common/api";
import config from "lib/config";
import { NavTreeContext } from "./context";

const { courseId, profileId, fromProfileId } = config;

const NavTreeProvider = ({ children }) => {
  const [state, setState] = useState({
    expanded: false,
    active: undefined,
    ready: false,
    nodes: [],
    isAdmin: false,
    studentView: fromProfileId || false,
  });

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const reload = useCallback(
    async (opts) => {
      const force = !!(opts && opts.force);
      if (!force && state.ready) return;

      if (!config.isLoggedIn) {
        updateState({
          ready: true,
          nodes: [],
          courses: [],
        });
        return;
      }

      const nodes = await GET(
        `/v1/courses/:courseId/content/navtree?${new URLSearchParams({
          profileId,
          fromProfileId: fromProfileId ? fromProfileId : 0,
        })}`,
        { courseId },
      );

      const isAdmin = await GET("/v1/user/isadminorteacherincourse/:courseId", {
        courseId,
      });

      updateState({
        nodes,
        isAdmin,
        studentView: fromProfileId || false,
        ready: true,
      });
    },
    [state.ready, updateState],
  );

  const getNodes = useCallback(async (callback) => {
    const nodes = await GET(
      `/v1/courses/:courseId/content/navtree?${new URLSearchParams({
        profileId,
        fromProfileId: fromProfileId ? fromProfileId : 0,
      })}`,
      {
        courseId,
      },
    );

    callback(nodes);
  }, []);

  const reloadNodes = useCallback(
    (nodes) => {
      updateState({ nodes });
    },
    [updateState],
  );

  const toggleNode = useCallback(
    (e) => {
      const nodeID = e.currentTarget.parentElement.id;
      const tempNodes = state.nodes.slice();

      const node = tempNodes.find((n) => n.navTreeID === nodeID);
      node.expanded = !node.expanded;
      updateState({ nodes: tempNodes });

      e.currentTarget.parentElement
        .querySelector("ul")
        .classList.toggle("contracted");
    },
    [state.nodes, updateState],
  );

  const setSelectedNode = useCallback(
    (navTreeId) => {
      const tempNodes = state.nodes.slice();
      const node = tempNodes.find((n) => n.navTreeID === navTreeId);
      const prevActive = tempNodes.find((n) => n.active === true);

      if (prevActive) {
        prevActive.active = false;
      }

      updateUrl(navTreeId);

      // Deselect active item in function menu
      if (typeof window !== "undefined" && window.eduutils) {
        window.eduutils.DeselectCourseMenu();
      }

      if (node) {
        node.active = true;
        expandToNode(tempNodes, node);
        if (state.active !== navTreeId) {
          updateState({ active: navTreeId, nodes: tempNodes });
        } else {
          updateState({ nodes: tempNodes });
        }
      } else {
        updateState({ nodes: tempNodes });
      }

      // Close menu if in mobile
      if (typeof window !== "undefined" && window.$) {
        if (window.$("[id*='mobilefooter']").is(":visible")) {
          // Only toggle if open
          if (window.$(".course.menu").hasClass("selected")) {
            window.eduutils.ToggleCourseMenu();
          }
        } else {
          // Close menu, when changing page if there is not enough room
          if (window.$(window).width() < window.eduutils.toggleMenuBreakPoint) {
            window.eduutils.ToggleCourseMenu();
          }
        }
      }
    },
    [state.nodes, state.active, updateUrl, expandToNode, updateState],
  );

  const updateUrl = useCallback((navTreeId) => {
    const stateObj = {
      navtreeid: "navtreeid",
    };
    let newUrl;
    let doUpdate = true;

    // replace navtreeid in url
    if (
      typeof window !== "undefined" &&
      window.location.href.indexOf("navtreeid") !== -1
    ) {
      const oldNavTreeId = window.luvit.GetQueryString("navtreeid");

      if (oldNavTreeId === navTreeId) {
        doUpdate = false;
      } else {
        newUrl = window.luvit.updateURLParameter(
          window.location.href,
          "navtreeid",
          navTreeId,
        );
      }
    } else {
      newUrl = document.location.href + "&navtreeid=" + navTreeId;
    }

    if (doUpdate) window.history.replaceState(stateObj, "", newUrl);
  }, []);

  const expandToNode = useCallback((tempNodes, node) => {
    if (node.parent && node.parent !== "0") {
      const parentNode = tempNodes.find((n) => n.navTreeID === node.parent);
      parentNode.expanded = true;

      if (parentNode.parent) {
        expandToNode(tempNodes, parentNode);
      }
    }
  }, []);

  const findFirstContentNode = useCallback(
    (nodes) => {
      if (!nodes) {
        nodes = state.nodes.filter((n) => n.parent === "0");
      }

      let firstContentNode;

      // Remove home node
      if (nodes[0] && nodes[0].typeID === 0) {
        nodes.shift();
      }

      // om första noden är ett dokument
      if (nodes[0] && nodes[0].typeID > 1) {
        firstContentNode = nodes[0];
      }

      if (firstContentNode) return firstContentNode.navTreeID;

      for (let i = 0, len = nodes.length; i < len; i++) {
        const { childNodes } = nodes[i];

        if (childNodes && childNodes.length > 0)
          return findFirstContentNode(childNodes);
      }
    },
    [state.nodes],
  );

  const getActive = useCallback(() => {
    const node = state.nodes.find((n) => n.active);

    if (node) return node.navTreeID;
  }, [state.nodes]);

  const isBookmarked = useCallback(
    (navTreeId) => {
      const node = state.nodes.find((n) => n.navTreeID === navTreeId);
      return node.isBookMarked;
    },
    [state.nodes],
  );

  const resetTree = useCallback(
    (keepState) => {
      const tempNodes = state.nodes.slice();

      for (let i = 0, len = tempNodes.length; i < len; i++) {
        if (!keepState) {
          tempNodes[i].expanded = false;
        }
        tempNodes[i].active = false;
      }

      updateState({ nodes: tempNodes });
    },
    [state.nodes, updateState],
  );

  const containsNode = useCallback(
    (navTreeId) => {
      const node = state.nodes.find((n) => n.navTreeID === navTreeId);

      return !!node;
    },
    [state.nodes],
  );

  const expandAll = useCallback(() => {
    const tempNodes = state.nodes.slice();

    tempNodes.forEach((n) => {
      n.expanded = true;
    });

    updateState({ nodes: tempNodes });
  }, [state.nodes, updateState]);

  const collapseAll = useCallback(() => {
    const tempNodes = state.nodes.slice();

    tempNodes.forEach((n) => {
      n.expanded = false;
    });

    updateState({ nodes: tempNodes });
  }, [state.nodes, updateState]);

  const value = {
    ...state,
    reload,
    getNodes,
    reloadNodes,
    toggleNode,
    setSelectedNode,
    findFirstContentNode,
    getActive,
    isBookmarked,
    resetTree,
    containsNode,
    expandAll,
    collapseAll,
  };

  return (
    <NavTreeContext.Provider value={value}>{children}</NavTreeContext.Provider>
  );
};

NavTreeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default NavTreeProvider;
